import { updNumber, updTitle } from "@/lib/receiving";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  PHOTO_CATEGORIES,
  PHOTO_CATEGORY_TITLES,
  downloadReviewPhotosZip,
  fetchReviewPhotoUrl,
  getReviewDelivery,
  type PhotoCategory,
  type ReviewPhoto,
} from "@/api/receiving";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { extractErrorMessage } from "@/lib/api-errors";
import { fmtDate } from "@/lib/format";
import { Lightbox } from "@/pages/receiving/Lightbox";
import { OkkBadge } from "@/pages/receiving/OkkBadge";
import { DeliveryStatusBadge } from "@/pages/receiving/ReviewListPage";

/**
 * Карточка ПРИХОДА в журнале (read-only).
 *
 * Показывает то, что реально приехало ЭТОЙ машиной: принятые партии с местами
 * и фото приёмки. Не строки заказа — журнал отвечает на вопрос «что привезли»,
 * а не «что заказывали», и по одному заказу машин бывает несколько.
 *
 * Фото грузятся из базы ПОРТАЛА: в 1С за картинками не ходим, иначе просмотр
 * журнала стоил бы ей десятков запросов на каждый приход.
 */
export function ReviewDeliveryPage(): React.ReactElement {
  const { deliveryId = "" } = useParams<{ deliveryId: string }>();
  const detailQ = useQuery({
    queryKey: ["rcv-review-delivery", deliveryId],
    queryFn: () => getReviewDelivery(deliveryId),
    enabled: Boolean(deliveryId),
  });
  const [lightbox, setLightbox] = useState<{
    photos: ReviewPhoto[];
    index: number;
  } | null>(null);

  const d = detailQ.data;
  const byCategory = (c: PhotoCategory) =>
    (d?.photos ?? []).filter((p) => p.category === c);

  // Одно фото — сразу jpg; несколько — zip (категория или всё по папкам).
  const downloadMut = useMutation({
    mutationFn: async (category?: PhotoCategory) => {
      const photos = category ? byCategory(category) : (d?.photos ?? []);
      if (category && photos.length === 1) {
        const url = await fetchReviewPhotoUrl(photos[0]!.id);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${PHOTO_CATEGORY_TITLES[category]} · УПД ${d?.header.incoming_number ?? ""} (1).jpg`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
        return;
      }
      await downloadReviewPhotosZip(deliveryId, category);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось скачать фото")),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-3">
      <div className="flex items-center gap-2">
        <Link
          to="/receiving-review"
          className="flex size-9 items-center justify-center rounded-md border hover:bg-accent"
          aria-label="К журналу"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h1 className="text-xl font-semibold">
          Приход {d ? `· ${updTitle(d.header)}` : ""}
        </h1>
        {d && <DeliveryStatusBadge row={d.header} />}
        {d && <OkkBadge okk={d.header.okk} className="text-xs" />}
        {d && d.photos.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            disabled={downloadMut.isPending}
            onClick={() => downloadMut.mutate(undefined)}
          >
            {downloadMut.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            Скачать все фото
          </Button>
        )}
      </div>

      {detailQ.isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {detailQ.isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          {extractErrorMessage(detailQ.error, "Не удалось загрузить приход")}
        </Card>
      )}

      {d && (
        <>
          <Card className="grid gap-x-6 gap-y-1 p-4 text-sm sm:grid-cols-2">
            <Field label="Приходная накладная">
              {d.header.receipt_number || "—"}
              {d.header.receipt_number && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {d.header.receipt_posted ? "проведена" : "не проведена"}
                </span>
              )}
            </Field>
            <Field label="УПД поставщика">
              {updNumber(d.header)}
              {d.header.incoming_date && (
                <span className="ml-2 text-xs text-muted-foreground">
                  от {fmtDate(d.header.incoming_date)}
                </span>
              )}
            </Field>
            <Field label="Заказ поставщику">{d.header.order_number || "—"}</Field>
            <Field label="Организация">{d.header.organization_name || "—"}</Field>
            <Field label="Поставщик">{d.header.kontragent_name || "—"}</Field>
            <Field label="Принял">
              {d.header.received_by || "—"}
              {d.header.completed_at && (
                <span className="ml-2 text-xs text-muted-foreground">
                  завершено {new Date(d.header.completed_at).toLocaleString("ru-RU")}
                </span>
              )}
            </Field>
          </Card>

          <Card className="overflow-hidden">
            <div className="flex items-baseline gap-3 border-b px-4 py-2">
              <span className="text-sm font-semibold">
                Принято · {d.batches.length}{" "}
                {d.batches.length === 1 ? "позиция" : "позиций"}
              </span>
              <span className="text-xs text-muted-foreground">
                мест: {d.batches.reduce((n, b) => n + b.places.length, 0)}
              </span>
            </div>
            {d.batches.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground">
                Позиций нет — приёмка была только фотофиксацией.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Артикул</th>
                      <th className="px-4 py-2 font-medium">Наименование</th>
                      <th className="px-4 py-2 font-medium">Партия 1С</th>
                      <th className="px-4 py-2 font-medium">Партия поставщика</th>
                      <th className="px-4 py-2 font-medium">Годен до</th>
                      <th className="px-4 py-2 text-right font-medium">Кол-во</th>
                      <th className="px-4 py-2 text-right font-medium">Мест</th>
                      <th className="px-4 py-2 font-medium">Штрихкод</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {d.batches.map((b) => (
                      <tr key={b.id}>
                        <td className="px-4 py-2 font-mono">
                          {b.nomenclature_article || "—"}
                        </td>
                        <td className="px-4 py-2">
                          {b.nomenclature_name}
                          {b.characteristic_name && (
                            <span className="text-muted-foreground">
                              {" "}
                              · {b.characteristic_name}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">{b.partia_number || "—"}</td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {b.supplier_batch || "—"}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {b.expiry_date ? fmtDate(b.expiry_date) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums">
                          {b.qty} {b.unit_name}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {b.places.length}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {b.barcode || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {PHOTO_CATEGORIES.map((cat) => {
            const photos = byCategory(cat);
            return (
              <Card key={cat} className="p-4">
                <div className="mb-2 flex items-center gap-1.5">
                  <span className="text-sm font-semibold">
                    {PHOTO_CATEGORY_TITLES[cat]}
                  </span>
                  {photos.length > 0 && (
                    <span className="rounded bg-muted px-1.5 text-xs text-muted-foreground">
                      {photos.length}
                    </span>
                  )}
                  {photos.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-8"
                      disabled={downloadMut.isPending}
                      onClick={() => downloadMut.mutate(cat)}
                    >
                      <Download className="size-4" /> Скачать
                    </Button>
                  )}
                </div>
                {photos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Фото нет</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {photos.map((p, i) => (
                      <ReviewThumb
                        key={p.id}
                        photo={p}
                        onClick={() => setLightbox({ photos, index: i })}
                      />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}

      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          orderNumber={d?.header.incoming_number || d?.header.order_number}
          onNavigate={(i) => setLightbox({ ...lightbox, index: i })}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function ReviewThumb({
  photo,
  onClick,
}: {
  photo: ReviewPhoto;
  onClick: () => void;
}): React.ReactElement {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objUrl = "";
    fetchReviewPhotoUrl(photo.id)
      .then((u) => {
        objUrl = u;
        if (cancelled) URL.revokeObjectURL(u); // размонтировались, пока грузилось
        else setUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [photo.id]);
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-40 overflow-hidden rounded-lg border bg-muted transition-opacity hover:opacity-90"
      aria-label="Открыть фото на полный экран"
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </button>
  );
}
